// База знаний «?» (README §11): статьи max-width 760 — заголовок Barlow 30,
// вводный абзац 14/1.6 --soft, пункты «название 14/500 + текст 13/1.6» с линией
// снизу. «Как оцениваем и перестраиваем» — критерии §4.5, «Типы элементов» —
// таблица §4.2 с пиктограммами, «Правила UI-кита» — с сервера
// (GET /api/sections/uikit, тот же текст уходит агенту-сборщику системным промптом).

import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { TypeIcon, TYPE_COLOR, TYPE_LABEL } from '../../ui/Icon'
import type { ElementType } from '../../ui/Icon'
import { SideMenu } from '../../ui/SideMenu'
import { describe } from '../custom/http'
import { sectionsApi } from '../custom/store'
import type { SectionModule } from '../types'
import './kb.css'

const ARTICLES = [
  { id: 'rebuild', title: 'Как оцениваем и перестраиваем' },
  { id: 'types', title: 'Типы элементов' },
  { id: 'uikit', title: 'Правила UI-кита' },
] as const
type ArticleId = (typeof ARTICLES)[number]['id']

const useKb = create<{ id: ArticleId; setId: (id: ArticleId) => void }>()((set) => ({
  id: 'rebuild',
  setId: (id) => set({ id }),
}))

function Sidebar() {
  const id = useKb((s) => s.id)
  const setId = useKb((s) => s.setId)
  return (
    <SideMenu
      screen="kb"
      sections={[{
        id: 'kb',
        title: 'Справочник',
        items: [{
          id: 'articles',
          label: 'справочник',
          children: ARTICLES.map((a) => ({ id: a.id, label: a.title, title: a.title, active: a.id === id, onSelect: () => setId(a.id) })),
        }],
      }]}
    />
  )
}

interface Point { name: string; text: string }

const REBUILD: Point[] = [
  { name: 'Запас до сброса', text: 'прогноз = скорость × время до сброса; сравниваем с остатком окна у провайдера' },
  { name: 'Доля элемента', text: 'сколько расхода даёт агент или субагент; предлагаем только для элементов с долей от 15 %' },
  { name: 'Сложность хода', text: 'длина ответа, число вызовов инструментов, глубина рассуждения — короткие и шаблонные ходы можно отдавать меньшей модели' },
  { name: 'Качество после смены', text: 'повторы, ошибки инструментов, откаты оператора за 20 ходов; при росте — вернуть прежний движок' },
  { name: 'Что не трогаем', text: 'оркестраторы с полными правами, элементы защиты, ходы с подтверждением оператора' },
  { name: 'Как применяется', text: 'после согласования меняется модель элемента; новый движок подхватывает следующий ход, текущий не прерывается' },
]

const TYPES: { type: ElementType; look: string; fill: string; role: string }[] = [
  { type: 'engine', look: '--agent, двойная рамка 3 px', fill: '12 %', role: 'модель, на которой работают агенты' },
  { type: 'orch', look: '--ok, рамка 2 px', fill: '18 %', role: 'точка управления: раздаёт работу субагентам' },
  { type: 'sub', look: '--ok', fill: '7 %', role: 'исполнитель по заданию оркестратора' },
  { type: 'skill', look: '--soft', fill: '10 %', role: 'инструкция-умение, подключается к агенту' },
  { type: 'mcp', look: '--warn, боковые планки 6 px', fill: '0', role: 'внешний сервис-инструмент' },
  { type: 'data', look: '--term-link', fill: '12 %', role: 'данные: файлы, база знаний, сессии' },
]

/** «1. **Боковое меню** — три уровня…» → {name, text}. */
export function parseRules(md: string): Point[] {
  const out: Point[] = []
  for (const raw of md.split('\n')) {
    const m = raw.match(/^\s*\d+\.\s+\*\*(.+?)\*\*\s*[—:-]?\s*(.*)$/)
    if (m) out.push({ name: m[1].replace(/[.:]$/, ''), text: m[2].replace(/\*\*/g, '') })
    else if (out.length && raw.trim() && !raw.startsWith('#')) out[out.length - 1].text += ' ' + raw.trim()
  }
  return out
}

function Points({ items }: { items: Point[] }) {
  return (
    <div className="abx-kb__points">
      {items.map((p) => (
        <div key={p.name} className="abx-kb__point">
          <div className="abx-kb__pname">{p.name}</div>
          <div className="abx-kb__ptext">{p.text}</div>
        </div>
      ))}
    </div>
  )
}

function Rebuild() {
  return (
    <>
      <h1 className="abx-kb__h1">Как оцениваем и перестраиваем</h1>
      <p className="abx-kb__lead">По каким признакам агент предлагает улучшения схемы и как они применяются после согласования.</p>
      <Points items={REBUILD} />
    </>
  )
}

function Types() {
  return (
    <>
      <h1 className="abx-kb__h1">Типы элементов</h1>
      <p className="abx-kb__lead">
        Типы на полотне Конструктора различаются заливкой, рамкой и пиктограммой. Слева у карточки — корешок цветом типа
        (кроме MCP). Пунктиров нет нигде, кроме черновика, который предложил агент.
      </p>
      <div className="abx-kb__tablewrap">
        <table className="abx-kb__table">
          <thead>
            <tr><th /><th>тип</th><th>цвет и рамка</th><th>заливка</th><th>роль</th></tr>
          </thead>
          <tbody>
            {TYPES.map((t) => (
              <tr key={t.type}>
                <td><TypeIcon type={t.type} width={26} /></td>
                <td className="abx-kb__tname" style={{ color: TYPE_COLOR[t.type] }}>{TYPE_LABEL[t.type]}</td>
                <td className="abx-kb__mono">{t.look}</td>
                <td className="abx-kb__mono">{t.fill}</td>
                <td>{t.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function Uikit() {
  const [rules, setRules] = useState<Point[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    sectionsApi.uikit().then((k) => setRules(parseRules(k.rules))).catch((e) => setError(describe(e)))
  }, [])
  return (
    <>
      <h1 className="abx-kb__h1">Правила UI-кита</h1>
      <p className="abx-kb__lead">
        По этим правилам агент собирает и правит разделы («+» и «✎»). Тот же текст уходит ему системным промптом.
      </p>
      {rules ? <Points items={rules} /> : <p className="abx-kb__lead">{error ? `правила не загрузились: ${error}` : 'загрузка…'}</p>}
    </>
  )
}

function View() {
  const id = useKb((s) => s.id)
  return (
    <div className="abx-kb">
      <article className="abx-kb__page">
        {id === 'rebuild' ? <Rebuild /> : id === 'types' ? <Types /> : <Uikit />}
      </article>
    </div>
  )
}

const kb: SectionModule = { Sidebar, View }
export default kb
